import React, { useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { PaymentProvider } from './contexts/PaymentContext';
import PayPalProvider from './components/PayPalProvider';
import ErrorBoundary from './components/ErrorBoundary';
import AppRoutes from './routes';
import ScrollToTop from './components/ScrollToTop';
import { optimizePageCSS } from './utils/cssOptimizer';
// 已通过GTM实现，不再使用独立组件
// import GoogleAdsTag from './components/common/GoogleAdsTag';

// 创建自定义主题
const theme = createTheme({
  palette: {
    primary: {
      main: '#9c27b0', // 紫色，匹配CSS变量--primary-color
      light: '#ba68c8',
      dark: '#7b1fa2',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#673ab7', // 深紫色，匹配CSS变量--secondary-color
      light: '#9575cd',
      dark: '#512da8',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    error: {
      main: '#f44336',
    },
    warning: {
      main: '#ff9800',
    },
    info: {
      main: '#2196f3',
    },
    success: {
      main: '#4caf50',
    },
  },
  typography: {
    fontFamily: [
      'Inter',
      'Roboto',
      'PingFang SC',
      'Microsoft YaHei',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    h1: {
      fontWeight: 600,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 12px 0 rgba(0,0,0,0.05)',
        },
      },
    },
  },
});

const App = () => {
  useEffect(() => {
    // 优化CSS - 在生产环境下启用CSS优化
    if (process.env.NODE_ENV === 'production') {
      optimizePageCSS();
    }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* 已通过GTM实现，不再使用独立组件 */}
      {/* <GoogleAdsTag /> */}
      <ErrorBoundary
        fallback={
          <div style={{ 
            padding: '20px',
            margin: '20px',
            textAlign: 'center',
            fontFamily: 'Arial, sans-serif'
          }}>
            <h2>很抱歉，应用程序遇到了问题</h2>
            <p>请尝试刷新页面或稍后再试</p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 20px',
                backgroundColor: '#9c27b0',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              刷新页面
            </button>
          </div>
        }
      >
        <AuthProvider>
          <CartProvider>
            <PaymentProvider>
              {/* 重新添加全局PayPalProvider，确保PayPal配置在应用启动时就加载 */}
              <PayPalProvider>
                <ScrollToTop />
                <AppRoutes />
              </PayPalProvider>
            </PaymentProvider>
          </CartProvider>
        </AuthProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
};

export default App;